import CloseIcon from '@offline-mui/icons-material/Close'
import SaveOutlinedIcon from '@offline-mui/icons-material/SaveOutlined'
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useMemo, useState } from 'react'
import type { ComponentProps } from 'react'
import { DateTimeField } from './ui/DateField'

/** Every editable field of a flight leg, exactly as the Blocks page stores it. */
export type SelectedFlightLeg = {
  key: string
  carrier: string
  flightNumber: string
  operatingCarrier: string
  operatingCarrierName: string
  origin: string
  destination: string
  departureDatetime: string
  arrivalDatetime: string
  aircraftType: string
  durationMin: string
  fareBasis: {
    cabin: string
    classOfService: string
    fareBasisCode: string
    brandCode: string
    brandName: string
  }
}

const CABINS = ['Economy', 'Premium Economy', 'Business', 'First']

type LegErrors = Partial<Record<string, string>>

function validateLeg(leg: SelectedFlightLeg): LegErrors {
  const errors: LegErrors = {}
  if (!leg.carrier.trim()) errors.carrier = 'Required'
  if (!leg.flightNumber.trim()) errors.flightNumber = 'Required'
  if (leg.origin.trim().length !== 3) errors.origin = '3-letter code'
  if (leg.destination.trim().length !== 3) errors.destination = '3-letter code'
  if (!leg.departureDatetime) errors.departureDatetime = 'Required'
  if (!leg.arrivalDatetime) errors.arrivalDatetime = 'Required'
  if (!leg.fareBasis.cabin) errors.cabin = 'Required'
  return errors
}

type FlightLegsEditorProps = {
  legs: SelectedFlightLeg[]
  onCancel: () => void
  onSave: (legs: SelectedFlightLeg[]) => void
}

export default function FlightLegsEditor({ legs, onCancel, onSave }: FlightLegsEditorProps) {
  const [draft, setDraft] = useState<SelectedFlightLeg[]>(() =>
    legs.map((leg) => ({ ...leg, fareBasis: { ...leg.fareBasis } })),
  )

  const errors = useMemo(() => draft.map(validateLeg), [draft])
  const isValid = errors.every((legErrors) => Object.keys(legErrors).length === 0)
  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(legs), [draft, legs])

  const update = (index: number, patch: Partial<Omit<SelectedFlightLeg, 'fareBasis'>>) => {
    setDraft((prev) => prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)))
  }
  const updateFare = (index: number, patch: Partial<SelectedFlightLeg['fareBasis']>) => {
    setDraft((prev) =>
      prev.map((leg, i) =>
        i === index ? { ...leg, fareBasis: { ...leg.fareBasis, ...patch } } : leg,
      ),
    )
  }

  const field = (
    index: number,
    key: keyof Omit<SelectedFlightLeg, 'fareBasis' | 'key'>,
    label: string,
    extra: Partial<ComponentProps<typeof TextField>> = {},
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft[index][key]}
      onChange={(event) => update(index, { [key]: event.target.value })}
      error={Boolean(errors[index][key])}
      helperText={errors[index][key]}
      {...extra}
    />
  )

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault()
        if (isValid) onSave(draft)
      }}
      sx={{ bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Stack divider={<Divider />}>
        {draft.map((leg, index) => (
          <Box key={leg.key} sx={{ px: { xs: 2, md: 2.5 }, py: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Flight {index + 1}</Typography>

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'carrier', 'Carrier', {
                  onChange: (event) => update(index, { carrier: event.target.value.toUpperCase() }),
                  slotProps: { htmlInput: { maxLength: 3 } },
                })}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'flightNumber', 'Flight number')}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'operatingCarrier', 'Operating carrier', {
                  onChange: (event) =>
                    update(index, { operatingCarrier: event.target.value.toUpperCase() }),
                  slotProps: { htmlInput: { maxLength: 3 } },
                })}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                {field(index, 'operatingCarrierName', 'Operating carrier name')}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                {field(index, 'aircraftType', 'Aircraft type')}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'origin', 'Origin', {
                  onChange: (event) => update(index, { origin: event.target.value.toUpperCase() }),
                  slotProps: { htmlInput: { maxLength: 3 } },
                })}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'destination', 'Destination', {
                  onChange: (event) =>
                    update(index, { destination: event.target.value.toUpperCase() }),
                  slotProps: { htmlInput: { maxLength: 3 } },
                })}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DateTimeField
                  size="small"
                  label="Departure"
                  value={leg.departureDatetime}
                  onChange={(value) => update(index, { departureDatetime: value })}
                  error={Boolean(errors[index].departureDatetime)}
                  helperText={errors[index].departureDatetime}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DateTimeField
                  size="small"
                  label="Arrival"
                  value={leg.arrivalDatetime}
                  onChange={(value) => update(index, { arrivalDatetime: value })}
                  error={Boolean(errors[index].arrivalDatetime)}
                  helperText={errors[index].arrivalDatetime}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                {field(index, 'durationMin', 'Duration (min)', {
                  slotProps: {
                    input: { readOnly: true },
                    htmlInput: { tabIndex: -1, 'aria-readonly': true },
                  },
                  sx: {
                    '& .MuiInputBase-root': { bgcolor: 'grey.100' },
                    '& .MuiInputBase-input': { color: 'text.secondary', cursor: 'default' },
                  },
                })}
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Cabin"
                  value={leg.fareBasis.cabin}
                  onChange={(event) => updateFare(index, { cabin: event.target.value })}
                  error={Boolean(errors[index].cabin)}
                  helperText={errors[index].cabin}
                >
                  {(CABINS.includes(leg.fareBasis.cabin) || !leg.fareBasis.cabin
                    ? CABINS
                    : [leg.fareBasis.cabin, ...CABINS]
                  ).map((cabin) => (
                    <MenuItem key={cabin} value={cabin}>
                      {cabin}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" sx={{ color: isValid ? 'text.secondary' : 'error.main' }}>
          {!isValid
            ? 'Fix the highlighted fields to save.'
            : isDirty
              ? 'Changes apply to this block when you click Update block.'
              : 'No changes yet.'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button type="button" variant="outlined" startIcon={<CloseIcon />} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            disabled={!isValid || !isDirty}
          >
            Save changes
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
